import { forwardRef, useMemo } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { CallIntelTypes, CallIntelUtils } from '@frontend/api-call-intel';
import { NoTranscription } from '@frontend/assets';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { ChipVariants, NakedUl } from '@frontend/design-system';
import { transcribingCallLottie } from '../assets/lotties';
import { useCallIntelDefaultsShallowStore } from '../hooks';
import { CallInsightBaseProps } from '../types';
import { callStatusUtils } from '../utils';
import { CallEmptyState } from './empty-state/call-empty-state';
import { AnalyzingCallLoader } from './loaders/analyzing-call-loader';
import { TranscriptCard } from './transcript-card';

export interface CallTranscriptProps extends CallInsightBaseProps {
  highlightedCitation?: CallIntelTypes.HighlightedCitation;
  containerStyles?: Interpolation<Theme>;
  callFlags: ReturnType<typeof callStatusUtils.getCallFlags>;
}

export const CallTranscript = forwardRef<HTMLUListElement, CallTranscriptProps>(
  ({ call, callFlags, callMetadata, highlightedCitation, isLoading, isDemoAccount, containerStyles }, ref) => {
    const { t } = useTranslation('callIntelInsights');

    const { isFailedCall, isSkippedCall } = callFlags;

    const { dataLabels, headerChipVariants } = useCallIntelDefaultsShallowStore(
      'chipVariants',
      'dataLabels',
      'headerChipVariants'
    );

    const callParsedTranscripts = useMemo(
      () =>
        !isDemoAccount
          ? CallIntelUtils.parseTranscript(callMetadata?.transcript as string)
          : CallIntelUtils.parseTranscript(callMetadata?.transcript as string, {
              PERSON_NAME: call?.person?.firstName ?? '',
            }),
      [callMetadata?.transcript, isDemoAccount, call?.person?.firstName]
    );

    const citations = useMemo(() => {
      return (callMetadata?.tasks || [])
        .map((task) => ({
          time: task.citationTime ?? '',
          text: task.citation ?? '',
          variant: (headerChipVariants[task.type].variant ?? 'neutral') as ChipVariants,
          popoverLabel: t(`{{taskType}} Task`, { taskType: dataLabels?.taskTypes?.[task.type] ?? task.type }),
        }))
        .filter((citation) => citation.time && citation.text && citation.variant)
        .sort((a, b) => b.text.length - a.text.length);
    }, [callMetadata, headerChipVariants, dataLabels, t]);

    const showLoading = callFlags.isProcessingCall || isLoading;

    return (
      <div css={[styles.containerStyles, containerStyles]}>
        {showLoading ? (
          <AnalyzingCallLoader lottie={transcribingCallLottie} message={t('Processing phone call...')} />
        ) : isFailedCall || isSkippedCall || !callMetadata?.transcript ? (
          <CallEmptyState
            mainText={t('No Transcript Available')}
            style={{
              overflow: 'auto',
            }}
            svg={NoTranscription}
            subText={t('Please review the call recording.')}
          />
        ) : (
          <div css={styles.content}>
            <NakedUl css={styles.transcriptList} ref={ref}>
              {callParsedTranscripts.map((transcript) => (
                <TranscriptCard
                  {...transcript}
                  caller={call?.person}
                  citations={citations}
                  highlightedCitation={highlightedCitation}
                  isDemoAccount={isDemoAccount}
                  key={transcript.time.start}
                  officeUser={call?.officeUser}
                  time={transcript.time.start}
                />
              ))}
            </NakedUl>
          </div>
        )}
      </div>
    );
  }
);

CallTranscript.displayName = 'CallTranscript';

const styles = {
  containerStyles: css`
    display: flex;
    flex-direction: column;
    overflow: auto;
  `,
  content: css`
    align-items: baseline;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    overflow: auto;
    position: relative;
  `,
  transcriptList: css`
    display: flex;
    flex-direction: column;
    overflow: auto;
    padding-right: ${theme.spacing(2)};

    &::-webkit-scrollbar {
      -webkit-appearance: none;
      width: ${theme.spacing(1)};
    }

    &::-webkit-scrollbar-thumb {
      background-color: ${theme.colors.neutral30};
      border-radius: ${theme.borderRadius.small};
    }
  `,
};
